import {
  AppDevice,
  AppDeviceCookieName,
  getAppDeviceCode,
} from "@/enums/device";
import i18n from "@/locales/index";
import Cookie from "@/modules/cookie";
import mergeWith from "lodash.mergewith";
import moment from "moment-timezone";
import { parse } from "tldts";

export const inBrowser = typeof window !== "undefined";
export const inApp = inBrowser && /Bingciyuan/.test(navigator.userAgent);
export const isUnikv = location.origin.includes("unikv");
export const isIOS = () =>
  inBrowser
    ? /ios|iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase())
    : false;

export function isMobile() {
  return (
    isBrowser() &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      window.navigator.userAgent
    )
  );
}

export function isAndroid() {
  return isBrowser() && /Android/i.test(navigator.userAgent);
}

export const isIOSWebview =
  /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari).*Bingciyuan/i.test(
    navigator.userAgent
  );
export const isAndroidWebview = navigator.userAgent.indexOf("wv") > -1;

export function isBrowser() {
  return typeof window !== "undefined";
}

export function base64ToFile(base64Data: string, fileName: string) {
  // Convert the base64 data to a binary string
  const byteString = atob(base64Data.split(",")[1]);

  // Get the mime type from the base64 data
  const mimeType = base64Data.split(",")[0].split(":")[1].split(";")[0];

  // Create a Uint8Array to hold the binary data
  const byteArray = new Uint8Array(byteString.length);

  // Fill the byteArray with the binary data
  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }

  // Create a Blob object from the byteArray and the mime type
  const blob = new Blob([byteArray], { type: mimeType });

  // Create a File object from the Blob and the file name
  const file = new File([blob], fileName, { type: mimeType });

  return file;
}

export function getDisplayDateTime(
  dateTimeString: string, // UTC时间戳
  withMs = false,
  forceHk = false,
  withCN = false
) {
  let dateTime: moment.Moment;
  // Parse the input date-time string if it has a value
  if (dateTimeString) {
    dateTime = moment(dateTimeString);
  } else {
    // Otherwise, use the current date-time
    dateTime = moment();
  }

  if (forceHk) {
    dateTime.tz("Asia/Hong_Kong");
  }

  if (withMs) {
    // 截取最后一位到倒数第四位
    const low = dateTimeString.slice(-4, -1);
    return `${dateTime.format("YYYY-MM-DD HH:mm:ss.SSS")}${low}`;
  } else if (withCN) {
    return `${dateTime.year()}年${
      dateTime.month() + 1
    }月${dateTime.date()}日 ${dateTime.hour()}:${dateTime.minute()}`;
  } else {
    return dateTime.format("YYYY-MM-DD HH:mm:ss");
  }
}

export function getServerTime(dateTimeString: string) {
  return moment(dateTimeString).tz("Asia/Hong_Kong");
}

export function getDisplayDate(dateTimeString, split = ".") {
  let date;
  // Parse the input date-time string if it has a value
  if (dateTimeString) {
    date = new Date(dateTimeString);
  } else {
    // Otherwise, use the current date-time
    date = new Date();
  }

  // Format the date-time string with padded zeros
  const formattedDate = `${date.getFullYear()}${split}${String(
    date.getMonth() + 1
  )}${split}${String(date.getDate())}`;
  return formattedDate;
}

export function timeAgo(timestamp, withSec = false) {
  const now = new Date();
  const inputTime = new Date(timestamp);
  const diffInSeconds = Math.floor((now - inputTime) / 1000);
  const isChinese = i18n.global.locale.value === "zh-CN";

  if (diffInSeconds < 60) {
    if (withSec) {
      return `${diffInSeconds}${isChinese ? "秒前" : "seconds ago"}`;
    }
    return isChinese ? "刚刚" : "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}${isChinese ? "分钟前" : "minutes ago"}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}${isChinese ? "小时前" : "hours ago"}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays <= 31) {
    return `${diffInDays}${isChinese ? "天前" : "days ago"}`;
  }
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths}${isChinese ? "个月前" : "months ago"}`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears}${isChinese ? "年前" : "years ago"}`;
}

export function getLanguage(id) {
  let chatLanguage;
  switch (id) {
    case 1:
      if (i18n.global.locale.value == "zh-CN") {
        chatLanguage = "英文";
      } else {
        chatLanguage = "English";
      }
      break;
    case 2:
      if (i18n.global.locale.value == "en-US") {
        chatLanguage = "Chinese";
      } else {
        chatLanguage = "中文";
      }
      break;
  }
  return chatLanguage;
}

const imgHandlerExt = ["", "/blur", "/pubic", "/raw", "/thumb"];

export function isImage(url: string) {
  if (url) {
    url = url.toLowerCase();
    return [".bmp", ".gif", ".jpg", ".jpeg", ".png", ".webp"].some((item) =>
      url.includes(item)
    );
  }
  return false;
}

export function isVideo(url: string) {
  return ["mp4", "mov", "mpg", "mpeg", "ogg", "webm"].some((item) =>
    url.endsWith(item)
  );
}

export function isAudio(url: string) {
  return ["mp3", "wav", "m4a", "ogg", "webm"].some((item) =>
    url.endsWith(item)
  );
}

export function isValidJSON(str: string) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

/**
 *
 * @param {string} text
 * @returns
 */
export function parseArray(text) {
  try {
    return JSON.parse(text);
  } catch (error) {
    const index = text.indexOf("[");
    if (index !== -1) {
      return parseArray(text.slice(index));
    }
  }
  return null;
}

/**
 *
 * @param {string} text
 * @returns
 */
export function parseObject(text) {
  try {
    return JSON.parse(text);
  } catch (error) {
    const index = text.indexOf('{"code');
    if (index !== -1) {
      return parseObject(text.slice(index));
    }
  }
  return null;
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function dateDifference(date1, date2) {
  // 确保date1总是小于date2
  if (date1 > date2) {
    [date1, date2] = [date2, date1];
  }
  const diffInSeconds = Math.floor((date2 - date1) / 1000);
  const days = Math.floor(diffInSeconds / (3600 * 24));
  const hours = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((diffInSeconds % 3600) / 60);
  const seconds = diffInSeconds % 60;

  return { days, hours, minutes, seconds };
}

export const copyToClipboard = (str: string) => {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);

  const selected =
    document.getSelection()!.rangeCount > 0
      ? document.getSelection()!.getRangeAt(0)
      : false;
  el.select();
  document.execCommand("copy");

  document.body.removeChild(el);
  if (selected) {
    document.getSelection()!.removeAllRanges();
    document.getSelection()!.addRange(selected);
  }
};

export const trim = (str: string, char?: string) =>
  char
    ? str.replace(new RegExp("^\\" + char + "+|\\" + char + "+$", "g"), "")
    : str.replace(/^\s+|\s+$/g, "");

export const getRegByTagText = (text: string) =>
  new RegExp(
    text.replace(/\(/g, "\\(").replace(/\)/g, "\\)") + "(:\\d+(\\.\\d+)?)?(,)?"
  );

export const getInitTraitPromptJson = () => ({
  faceExpression: { prompt: "", negativePrompt: "" },
  pose: { prompt: "", negativePrompt: "" },
  clothing: { prompt: "", negativePrompt: "" },
});

export const getInitPromptJson = (): PromptJson => ({
  env: { prompt: "", negativePrompt: "", weight: 0.6 },
  faceExpression: { prompt: "", negativePrompt: "" },
  pose: { prompt: "", negativePrompt: "" },
  clothing: { prompt: "", negativePrompt: "" },
  ratio: "3:4",
  amount: 1,
  style: 0,
  prompt: "",
  negativePrompt: "",
  matePrompt: "",
  mateNegativePrompt: "",
  mixStyle: [],
  customStyle: { version: 1, prompt: "" },
  mates: [],
  isAddNSFWTag: true,
  isUnlimited: false,
  featureType: 1,
  isNotShown: false,
});

export function tryParse(str: string) {
  try {
    return JSON.parse(str);
  } catch {
    return undefined;
  }
}

export async function readFileAsArrayBufferSync(
  file: File
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      resolve(new Uint8Array(event.target!.result as ArrayBuffer));
    };
    reader.onerror = function (event) {
      reject(event.target!.error);
    };

    reader.readAsArrayBuffer(file);
  });
}

export async function readFileAsTextSync(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      resolve(event.target!.result as string);
    };
    reader.onerror = function (event) {
      reject(event.target!.error);
    };

    reader.readAsText(file, "utf-8");
  });
}

export function setAppDeviceCookieIfAbsent() {
  const res = parse(location.href);
  if (!res.domain) return;
  const domain = `.${res.domain}`;
  // if (document.cookie.includes(`${AppDeviceCookieName}=`)) return;
  if (isIOSWebview) {
    Cookie.set(AppDeviceCookieName, AppDevice.IOS, { expires: 365 }, domain);
  } else if (isAndroidWebview) {
    Cookie.set(AppDeviceCookieName, AppDevice.APK, { expires: 365 }, domain);
  }
}

export function getDeviceCode() {
  const device = Cookie.get(AppDeviceCookieName);
  return getAppDeviceCode(device as AppDevice | undefined);
}

export function isInvalidDistribution() {
  return isIOSWebview;
}

export const clearCache = () => {
  caches.keys().then(function (names) {
    for (const name of names) caches.delete(name);
  });
  localStorage.clear();
  location.reload();
  // service workers 清理
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then(function (registrations) {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  }
};

export const promptListRemoveTag = (promptList: string, removePrompt: string) =>
  promptList
    .replace(
      new RegExp(
        `(?:^|,)(\\s*)${removePrompt.replace(
          /[-\/\\^$*+?.()|[\]{}]/g,
          "\\$&"
        )}(?::[^,]*)?(?=,|$)`,
        "g"
      ),
      ""
    )
    .replace(/(^,|,$)/g, "")
    .replace(/,,+/g, ",")
    .trim();

export const mergeIgnoreEmpty = (target: object, source: object) => {
  mergeWith(target, source, (a, b) =>
    b === null || b === undefined || (Array.isArray(b) && b.length === 0)
      ? a
      : undefined
  );
};
